'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function RentCalculator() {
  const t = useTranslations("tools.rentCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  const [inputs, setInputs] = useState({
    income: 80000,
    incomePeriod: 'yearly',
    monthlyDebt: 0
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (amount: number) => {
    return '$' + Math.round(amount).toLocaleString('en-US');
  };

  const formatPercent = (decimal: number) => {
    return (decimal * 100).toFixed(1) + '%';
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { income, incomePeriod, monthlyDebt } = inputs;

    if (!income || income <= 0) {
      alert(t('validationIncome'));
      return;
    }

    let monthlyIncome = incomePeriod === 'yearly' ? income / 12 : income;

    const recommendedRent = monthlyIncome * 0.30;
    const maxRent = monthlyIncome * 0.33;

    const availableIncome = monthlyIncome - monthlyDebt;
    const remainingAfterRent = monthlyIncome - recommendedRent - monthlyDebt;
    const rentRatio = recommendedRent / monthlyIncome;

    let affordabilityLevel = 'good';
    let affordabilityText = t('affordabilityGood');

    if (monthlyDebt / monthlyIncome > 0.20) {
      affordabilityLevel = 'moderate';
      affordabilityText = t('affordabilityModerate');
    }

    if (monthlyDebt / monthlyIncome > 0.36 || remainingAfterRent < monthlyIncome * 0.30) {
      affordabilityLevel = 'high';
      affordabilityText = t('affordabilityHigh');
    }

    if (recommendedRent < 0 || availableIncome < 0) {
      affordabilityLevel = 'high';
      affordabilityText = t('affordabilityCritical');
    }

    setResults({
      monthlyIncome,
      recommendedRent: Math.max(0, recommendedRent),
      maxRent: Math.max(0, maxRent),
      monthlyDebt,
      availableIncome: Math.max(0, availableIncome),
      remainingAfterRent: Math.max(0, remainingAfterRent),
      rentRatio,
      affordabilityLevel,
      affordabilityText
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolSubtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginTop: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: '#334155',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const helpIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    background: '#e0f2fe',
    color: '#0284c7',
    borderRadius: '50%',
    fontSize: '0.75rem',
    cursor: 'help',
    fontWeight: 700
  };

  const inputWrapperStyle = {
    position: 'relative' as const,
    display: 'flex',
    gap: '0.75rem'
  };

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      background: 'white',
      flex: 1,
      outline: 'none'
    },
    {},
    {},
    {}
  );

  const inputWithPrefixStyle = {
    ...inputStyle,
    paddingLeft: '2rem'
  };

  const selectStyle = getResponsiveStyle(
    {
      ...inputStyle,
      cursor: 'pointer',
      minWidth: '140px',
      flex: 'none' as const
    },
    { minWidth: '120px' },
    { minWidth: '140px' },
    { minWidth: '140px' }
  );

  const inputPrefixStyle = {
    position: 'absolute' as const,
    left: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
    pointerEvents: 'none' as const
  };

  const inputHintStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.25rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.85rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      fontWeight: 600,
      borderRadius: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: '100%',
      marginTop: '1.5rem',
      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1rem 2.5rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      animation: 'slideIn 0.4s ease'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { color: colors.textPrimary, marginBottom: '1.5rem', textAlign: 'center' as const, fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const resultCardsStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      border: '2px solid #bae6fd'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultCardPrimaryStyle = {
    ...resultCardStyle,
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    borderColor: '#7c3aed'
  };

  const resultCardLabelStyle = getResponsiveStyle(
    { fontWeight: 600, marginBottom: '0.5rem', opacity: 0.9 },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const resultCardValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const resultDetailsStyle = getResponsiveStyle(
    { background: '#f8fafc', borderRadius: '0.75rem', marginTop: '1.5rem' },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e2e8f0'
  };

  const resultLabelStyle = getResponsiveStyle(
    { color: '#64748b', fontWeight: 500 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const affordabilityIndicatorStyle = getResponsiveStyle(
    { marginTop: '1.5rem', borderRadius: '0.75rem', textAlign: 'center' as const, fontWeight: 600 },
    { padding: '1rem', fontSize: '0.9rem' },
    { padding: '1.15rem', fontSize: '0.95rem' },
    { padding: '1.25rem', fontSize: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { marginLeft: '1.5rem', color: colors.textSecondary, lineHeight: 1.7 },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.125rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.15rem' }
  );

  const referenceStyle = {
    marginBottom: '0.75rem'
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    transition: 'color 0.3s ease'
  };

  const getAffordabilityStyle = (level: string) => {
    const baseStyle = affordabilityIndicatorStyle;

    if (level === 'good') {
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
        color: '#166534',
        border: '2px solid #86efac'
      };
    } else if (level === 'moderate') {
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        color: '#92400e',
        border: '2px solid #fcd34d'
      };
    } else {
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
        color: '#991b1b',
        border: '2px solid #fca5a5'
      };
    }
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
            <p style={toolSubtitleStyle}>{t('toolSubtitle')}</p>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="income">
                {t('incomeLabel')}
                <span style={helpIconStyle} title={t('incomeTooltip')}>?</span>
              </label>
              <div style={inputWrapperStyle}>
                <div style={{ position: 'relative' as const, flex: 1 }}>
                  <span style={inputPrefixStyle}>$</span>
                  <input
                    type="number"
                    id="income"
                    style={inputWithPrefixStyle}
                    placeholder="80000"
                    min="0"
                    step="1000"
                    value={inputs.income}
                    onChange={(e) => setInputs({...inputs, income: parseFloat(e.target.value) || 0})}
                    required
                  />
                </div>
                <select
                  id="income-period"
                  style={selectStyle}
                  value={inputs.incomePeriod}
                  onChange={(e) => setInputs({...inputs, incomePeriod: e.target.value})}
                >
                  <option value="yearly">{t('perYear')}</option>
                  <option value="monthly">{t('perMonth')}</option>
                </select>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="monthly-debt">
                {t('debtLabel')}
                <span style={helpIconStyle} title={t('debtTooltip')}>?</span>
              </label>
              <div style={{ position: 'relative' as const }}>
                <span style={inputPrefixStyle}>$</span>
                <input
                  type="number"
                  id="monthly-debt"
                  style={inputWithPrefixStyle}
                  placeholder="0"
                  min="0"
                  step="100"
                  value={inputs.monthlyDebt}
                  onChange={(e) => setInputs({...inputs, monthlyDebt: parseFloat(e.target.value) || 0})}
                />
              </div>
              <span style={inputHintStyle}>{t('debtHint')}</span>
            </div>

            <button
              type="submit"
              style={calculateBtnStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(37, 99, 235, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(37, 99, 235, 0.3)';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>

              <div style={resultCardsStyle}>
                <div style={resultCardPrimaryStyle}>
                  <div style={{...resultCardLabelStyle, opacity: 1}}>{t('recommendedRent')}</div>
                  <div style={{...resultCardValueStyle, color: 'white'}}>{formatCurrency(results.recommendedRent)}</div>
                </div>

                <div style={resultCardStyle}>
                  <div style={resultCardLabelStyle}>{t('maxRent')}</div>
                  <div style={resultCardValueStyle}>{formatCurrency(results.maxRent)}</div>
                </div>
              </div>

              <div style={resultDetailsStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('monthlyIncome')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.monthlyIncome)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('monthlyDebt')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.monthlyDebt)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('availableIncome')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.availableIncome)}</span>
                </div>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('rentRatio')}</span>
                  <span style={resultValueStyle}>{formatPercent(results.rentRatio)}</span>
                </div>
                <div style={{...resultItemStyle, borderBottom: 'none'}}>
                  <span style={resultLabelStyle}>{t('remainingIncome')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.remainingAfterRent)}</span>
                </div>
              </div>

              <div style={getAffordabilityStyle(results.affordabilityLevel)}>
                {results.affordabilityText}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introPara1')}</p>
        <p style={paragraphStyle}>{t('introPara2')}</p>
        <p style={paragraphStyle}>{t('introPara3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Desc')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Desc')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Desc')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Desc')}
          </li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsPara1')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t('thirtyPercentRule')}</h3>
        <p style={paragraphStyle}>{t('thirtyPercentRuleDesc')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t('debtToIncomeTitle')}</h3>
        <p style={paragraphStyle}>{t('debtToIncomeDesc')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t('costBurdenedTitle')}</h3>
        <p style={paragraphStyle}>{t('costBurdenedDesc')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('practice1Title')}</strong> {t('practice1Desc')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('practice2Title')}</strong> {t('practice2Desc')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('practice3Title')}</strong> {t('practice3Desc')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('practice4Title')}</strong> {t('practice4Desc')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('practice5Title')}</strong> {t('practice5Desc')}
          </li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('practice6Title')}</strong> {t('practice6Desc')}
          </li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={paragraphStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={referenceStyle}>
          <a
            href="https://www.novoco.com/resource-centers/affordable-housing-tax-credits/rent-income-limit-calculator"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('ref1')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a
            href="https://edinhart.com/rental-property-roi-calculator/"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('ref2')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a
            href="https://join.globalvacationrentals.com/blog/rental-income-calculator/"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('ref3')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a
            href="https://www.fibt.com/education-center/banking-calculators/buy-vs-rent-calculator/"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('ref4')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a
            href="https://www.hemlane.com/resources/rental-calculator/"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('ref5')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a
            href="https://www.rentometer.com/rent-calculator"
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('ref6')}
          </a>
        </div>
      </section>
    </div>
  );
}